import {
  usePostCustomerAuthForgotPassword,
  usePostCustomerAuthLogin,
  usePostCustomerAuthRegister,
  usePostCustomerAuthResetPassword,
} from '@agos/api-client/customers'
import { T, tt } from '@agos/shared'
import { Eye, EyeOff, KeyRound, LogIn, UserPlus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation, useSearchParams } from 'wouter'

import { lastEmail, useCustomerAuth } from '@agos/customer-core'
import {
  customerUserFrom,
  failureNote,
  mayRegister,
  mayResetPassword,
  maySignIn,
  newPasswordProblem,
  normaliseEmail,
  registrationProblems,
  sessionFrom,
  type RegistrationForm,
} from './credentials'

/**
 * Sign in, register, reset — the three pages that get a customer an account.
 *
 * Their shared rules live in `credentials.ts`. Two decisions from the C0 spec are load-bearing here:
 *
 * **A 2xx without both `user` and `token` is a failure.** Every legacy page checks this and it is kept —
 * a half-answer that is accepted leaves the app looking signed in and unable to make one authorised call.
 *
 * **The reset form never says whether an address is known.** Any 2xx shows the same panel. A form that
 * distinguishes *sent* from *no such account* is an account-enumeration oracle, and legacy gets this
 * right; it is kept deliberately rather than by accident.
 */

const INPUT =
  'w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors'

function Field(props: {
  id: string
  label: string
  type?: string
  autoComplete?: string
  inputMode?: 'tel' | 'email'
  placeholder?: string
  value: string
  onChange: (value: string) => void
  problem?: string
  /** Adds the eye toggle legacy puts inside the password box. */
  revealable?: boolean
}) {
  const [reveal, setReveal] = useState(false)
  const type = props.revealable === true ? (reveal ? 'text' : 'password') : (props.type ?? 'text')

  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="text-sm font-medium text-foreground">
        {props.label} <span className="text-destructive">*</span>
      </label>
      <div className={props.revealable === true ? 'relative' : undefined}>
        <input
          id={props.id}
          data-field={props.id}
          type={type}
          inputMode={props.inputMode}
          autoComplete={props.autoComplete}
          placeholder={props.placeholder}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          className={props.revealable === true ? `${INPUT} pr-10` : INPUT}
        />
        {props.revealable === true && (
          // Out of the tab order, so it never sits between the password and the submit button.
          <button
            type="button"
            tabIndex={-1}
            data-reveal-password
            aria-label={reveal ? tt('Skryť heslo') : tt('Zobraziť heslo')}
            onClick={() => setReveal(!reveal)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {props.problem !== undefined && props.problem !== '' && (
        <span className="block text-[11px] text-red-500" data-field-problem={props.id}>
          {props.problem}
        </span>
      )}
    </div>
  )
}

const SUBMIT =
  'w-full flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-semibold py-3 text-sm hover:opacity-90 disabled:opacity-60 transition-opacity'

function Shell(props: {
  testId: string
  icon: typeof LogIn
  title: ReactNode
  subtitle: ReactNode
  children: ReactNode
}) {
  const Icon = props.icon
  return (
    <main className="max-w-lg mx-auto px-4 py-8 overflow-x-hidden" data-auth-page={props.testId}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">{props.title}</h1>
          <p className="text-sm text-muted-foreground">{props.subtitle}</p>
        </div>
      </div>
      {props.children}
    </main>
  )
}

/** The message a failed submit leaves, in legacy's panel rather than as loose red text. */
function Problem({ text }: { text: string }) {
  if (text === '') return null
  return (
    <div
      data-auth-problem
      className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
    >
      {text}
    </div>
  )
}

export function SignInPage() {
  const [, navigate] = useLocation()
  const signIn = useCustomerAuth((state) => state.signIn)
  const login = usePostCustomerAuthLogin()

  // Prefilled from the last sign-in on this device, which sign-out clears.
  const [email, setEmail] = useState(lastEmail)
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState('')

  async function submit() {
    setProblem('')
    try {
      const answer = await login.mutateAsync({
        data: { email: normaliseEmail(email), password },
      })
      const session = sessionFrom(answer)
      if (session === null) {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      // §10A.120 - read field by field rather than cast. The cast this replaces is what let the
      // login response carry five fields into a type declaring eight.
      const account = customerUserFrom(session.user)
      if (account === null) {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      signIn(account, session.token)
      navigate('/moj-profil')
    } catch (failure) {
      setProblem(failureNote(failure, tt('Prihlásenie zlyhalo')))
    }
  }

  return (
    <Shell
      testId="sign-in"
      icon={LogIn}
      title={<T>Prihlásenie</T>}
      subtitle={<T>Prihláste sa do svojho účtu</T>}
    >
      {/* `noValidate`: the page owns its messages, so the browser's own bubbles never appear. */}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        className="space-y-3"
      >
        <Field
          id="email"
          label={tt('E-mail')}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="jan.novak@email.sk"
          value={email}
          onChange={setEmail}
        />
        <div className="space-y-1.5">
          <Field
            id="password"
            label={tt('Heslo')}
            autoComplete="current-password"
            placeholder={tt('Vaše heslo')}
            revealable
            value={password}
            onChange={setPassword}
          />
          <div className="text-right">
            <Link href="/reset-hesla" data-to-reset className="text-xs text-primary hover:underline">
              <T>Zabudnuté heslo?</T>
            </Link>
          </div>
        </div>
        <Problem text={problem} />
        <button
          type="submit"
          data-submit
          disabled={!maySignIn(email, password) || login.isPending}
          className={SUBMIT}
        >
          <LogIn className="w-4 h-4" />
          <T>Prihlásiť sa</T>
        </button>
        <p className="text-center text-sm text-muted-foreground pt-2">
          <T>Nemáte účet?</T>{' '}
          <Link href="/registracia" data-to-register className="text-primary font-medium hover:underline">
            <T>Zaregistrujte sa</T>
          </Link>
        </p>
      </form>
    </Shell>
  )
}

export function RegisterPage() {
  const [, navigate] = useLocation()
  const signIn = useCustomerAuth((state) => state.signIn)
  const register = usePostCustomerAuthRegister()

  const [form, setForm] = useState<RegistrationForm>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [problem, setProblem] = useState('')

  const problems = registrationProblems(form)
  const change = (field: keyof RegistrationForm) => (value: string) => {
    setForm({ ...form, [field]: value })
    setTouched({ ...touched, [field]: true })
  }
  // A field says what is wrong only once it has been touched — a form that is red before it is filled
  // is telling the customer off for not having started.
  const shownProblem = (field: keyof RegistrationForm) =>
    touched[field] === true ? problems[field] : undefined

  async function submit() {
    setProblem('')
    try {
      const answer = await register.mutateAsync({
        data: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim(),
          email: normaliseEmail(form.email),
          password: form.password,
        },
      })
      const session = sessionFrom(answer)
      if (session === null) {
        setProblem(tt('Registrácia zlyhala'))
        return
      }
      // §10A.120 - read field by field rather than cast. The cast this replaces is what let the
      // login response carry five fields into a type declaring eight.
      const account = customerUserFrom(session.user)
      if (account === null) {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      signIn(account, session.token)
      navigate('/moj-profil')
    } catch (failure) {
      // 409 when the address already has an account — "Účet s týmto emailom už existuje. Prihláste sa."
      setProblem(failureNote(failure, tt('Registrácia zlyhala')))
    }
  }

  return (
    <Shell
      testId="register"
      icon={UserPlus}
      title={<T>Registrácia</T>}
      subtitle={<T>Zapojte sa do vernostného programu</T>}
    >
      {/* Legacy says what registering *does* before asking for anything - the loyalty programme is the
          reason to fill this in, and the port had dropped both the panel and the subtitle that name it
          (FB-C54). */}
      <div className="bg-secondary/30 border border-border rounded-2xl p-4 mb-6">
        <p className="text-sm text-foreground">
          <T>
            Registráciou sa automaticky zapojíte do nášho vernostného programu a získate prístup k
            exkluzívnym zľavám a ponukám.
          </T>
        </p>
      </div>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        className="space-y-3"
      >
        {/* A first and last name are one line in legacy, and one line is what they read as. */}
        <div className="grid grid-cols-2 gap-4">
          <Field id="first-name" label={tt('Meno')} placeholder={tt('Ján')} autoComplete="given-name" value={form.firstName} onChange={change('firstName')} problem={shownProblem('firstName')} />
          <Field id="last-name" label={tt('Priezvisko')} placeholder={tt('Novák')} autoComplete="family-name" value={form.lastName} onChange={change('lastName')} problem={shownProblem('lastName')} />
        </div>
        <Field id="phone" label={tt('Telefónne číslo')} placeholder="+421 900 000 000" inputMode="tel" autoComplete="tel" value={form.phone} onChange={change('phone')} problem={shownProblem('phone')} />
        <Field id="email" label={tt('E-mail')} placeholder="jan.novak@email.sk" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={change('email')} problem={shownProblem('email')} />
        <Field id="password" label={tt('Heslo')} placeholder={tt('Aspoň 6 znakov')} revealable autoComplete="new-password" value={form.password} onChange={change('password')} problem={shownProblem('password')} />
        <Field id="confirm-password" label={tt('Potvrdiť heslo')} placeholder={tt('Zadajte heslo znova')} revealable autoComplete="new-password" value={form.confirmPassword} onChange={change('confirmPassword')} problem={shownProblem('confirmPassword')} />
        <Problem text={problem} />
        <button
          type="submit"
          data-submit
          disabled={!mayRegister(form) || register.isPending}
          className={SUBMIT}
        >
          <UserPlus className="w-4 h-4" />
          <T>Zaregistrovať sa</T>
        </button>
        <p className="text-center text-sm text-muted-foreground pt-2">
          <T>Už máte účet?</T>{' '}
          <Link href="/prihlasenie" data-to-sign-in className="text-primary font-medium hover:underline">
            <T>Prihláste sa</T>
          </Link>
        </p>
      </form>
    </Shell>
  )
}

export function ResetPasswordPage() {
  const [, navigate] = useLocation()
  const signIn = useCustomerAuth((state) => state.signIn)
  const forgot = usePostCustomerAuthForgotPassword()
  const reset = usePostCustomerAuthResetPassword()

  // Read through wouter rather than `window.location.search` during render, so a URL change is picked
  // up without a remount. Same value, and reactive.
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [email, setEmail] = useState(lastEmail)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [sent, setSent] = useState(false)
  const [problem, setProblem] = useState('')

  async function ask() {
    setProblem('')
    try {
      await forgot.mutateAsync({ data: { email: normaliseEmail(email) } })
      setSent(true)
    } catch (failure) {
      setProblem(failureNote(failure, tt('Nepodarilo sa odoslať e-mail')))
    }
  }

  async function apply() {
    setProblem('')
    try {
      const answer = await reset.mutateAsync({ data: { token, password } })
      const session = sessionFrom(answer)
      if (session === null) {
        setProblem(tt('Heslo sa nepodarilo zmeniť'))
        return
      }
      // §10A.120 - read field by field rather than cast. The cast this replaces is what let the
      // login response carry five fields into a type declaring eight.
      const account = customerUserFrom(session.user)
      if (account === null) {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      signIn(account, session.token)
      navigate('/moj-profil')
    } catch (failure) {
      // An expired or reused token comes back as a server error and is shown word for word.
      setProblem(failureNote(failure, tt('Heslo sa nepodarilo zmeniť')))
    }
  }

  if (token !== '') {
    return (
      <Shell
        testId="reset-with-token"
        icon={KeyRound}
        title={<T>Nové heslo</T>}
        subtitle={<T>Zvoľte si nové heslo k svojmu účtu</T>}
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            void apply()
          }}
          className="space-y-3"
        >
          <Field id="password" label={tt('Nové heslo')} type="password" autoComplete="new-password" value={password} onChange={setPassword} />
          <Field
            id="confirm-password"
            label={tt('Nové heslo znova')}
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={setConfirmation}
            problem={newPasswordProblem(password, confirmation)}
          />
          <button
            type="submit"
            data-submit
            disabled={!mayResetPassword(password, confirmation) || reset.isPending}
            className={SUBMIT}
          >
            <T>Nastaviť heslo</T>
          </button>
        </form>
        <Problem text={problem} />
      </Shell>
    )
  }

  return (
    <Shell
      testId="reset"
      icon={KeyRound}
      title={<T>Zabudnuté heslo</T>}
      subtitle={<T>Zadajte váš email a pošleme vám odkaz na obnovenie</T>}
    >
      {sent ? (
        /* The same panel whatever the answer was. Saying *sent* for a known address and something else
           for an unknown one would tell anybody who asks which addresses have accounts. */
        <p className="rounded-2xl border border-border bg-card p-3 text-sm" data-reset-sent>
          <T>
            Ak k tejto adrese patrí účet, poslali sme naň odkaz na zmenu hesla. Skontrolujte si aj
            priečinok so spamom.
          </T>
        </p>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            void ask()
          }}
          className="space-y-3"
        >
          <Field id="email" label={tt('E-mail')} placeholder="jan.novak@email.sk" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} />
          <button
            type="submit"
            data-submit
            disabled={email.trim() === '' || forgot.isPending}
            className={SUBMIT}
          >
            <KeyRound className="w-4 h-4" />
            <T>Obnoviť heslo</T>
          </button>
        </form>
      )}
      <Problem text={problem} />
      <p className="text-center text-sm text-muted-foreground pt-2">
        <Link href="/prihlasenie" data-to-sign-in className="text-primary font-medium hover:underline">
          <T>Späť na prihlásenie</T>
        </Link>
      </p>
    </Shell>
  )
}
